import { Category, FirstCategoryLevel } from 'src/api/types';
import { BookingStep } from 'src/page-components/checkout/types';
import { Locales } from './localization';

export type SearchPageQueryType = {
  search?: string;
  page?: number;
  filters?: string;
  sortBy?: string;
  sortDirection?: string;
  // Tradename fields
  drug?: string;
  params?: string;
};

export interface CategoryPageQueryType extends SearchPageQueryType {
  page?: number;
  category: Category;
  subCategoryId?: string;
}
export interface MultyLevelCategoryPageQueryType extends SearchPageQueryType {
  page?: number;
  category: FirstCategoryLevel;
  subCategoryId?: string;
  secondCategoryId?: string;
  thirdCategoryId?: string;
}

export type GeneratedLinkUrlObject = {
  pathname: string;
  query?: SearchPageQueryType;
  hash?: string;
};

export interface LetterPageQueryType {
  page?: number;
  letter: string;
}

export type GeneratedLetterPageUrlObject = {
  pathname: string;
  query?: LetterPageQueryType;
};

export interface PageQueryType {
  page?: number;
}

export const instagramLink = 'https://www.instagram.com/likie.ua/';
export const facebookLink = 'https://www.facebook.com/Likie-108143788321320';
export const telegramLink = 'https://t.me/joinchat/WBDwS6HPq_Q1NzMy';
export const telegramBotLink = 'https://t.me/likieua_bot';
export const viberBotLink = 'https://www.viber.com/likieua_bot';

export const prodAPI = 'https://api.likie.dp.ua/';
export const bookingStatusUpdate = `${prodAPI}partner/api/order/status`;
export const receivingNewBookingsAllPharmacies = `${prodAPI}partner/api/order`;
export const receivingNewBookingsSelectedPharmacy = `${prodAPI}partner/api/order/branch/123`;
export const loadingPharmacies = `${prodAPI}partner/api/branch`;
export const prodswagger = `${prodAPI}swagger-ui.html`;
export const leftovers = `${prodAPI}partner/api/rest`;
export const cancelledOrder = `${prodAPI}partner/api/order/status/CANCELED_BY_LIKIE`;

export const supportPhoneNum = '0800 333 132';
export const supportPhoneNumLink = `tel:0800333132`;

// Purge null and undefined fields
const purgeNullAndUndefined = (obj: { [key: string]: string | number }) => {
  return Object.entries(obj).reduce(
    // eslint-disable-next-line
    (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
    {}
  );
};

export const seoFriendlyPageParam = (page: number) =>
  page && page !== 1 ? `page-${page}/` : '';

export const seoFriendlyCityParam = (cityName?: string) =>
  cityName ? `${cityName}/` : '';

export const extractPageNumber = (pageValue: string | undefined): number => {
  return pageValue ? parseInt(pageValue.split('page-')[1], 10) : 1;
};

interface ProductPathnameProps {
  translitName?: string;
  locale?: string;
}

const getProductPathname = ({
  translitName,
  locale
}: ProductPathnameProps): string => {
  const prefix = locale && locale !== Locales.UA ? `/${locale}` : '';

  return `${prefix}/product/${translitName}`;
};

export const generateDrugPageLink = (
  props: ProductPathnameProps
): GeneratedLinkUrlObject => {
  return {
    pathname: getProductPathname(props)
  };
};
export const generateDrugPageReviewsLink = (
  props: ProductPathnameProps
): GeneratedLinkUrlObject => {
  return {
    pathname: getProductPathname(props),
    hash: 'reviews'
  };
};

export const generateDrugPageReviewFormLink = (
  props: ProductPathnameProps
): GeneratedLinkUrlObject => {
  return {
    pathname: getProductPathname(props),
    hash: 'reviewsform'
  };
};

export const generateArticlePageLink = ({
  translitName
}: {
  translitName?: string;
}): GeneratedLinkUrlObject => {
  return {
    pathname: `/article/${translitName}/`
  };
};

export const generateHomePageLink = (): GeneratedLinkUrlObject => {
  return {
    pathname: '/'
  };
};

export const generateSearchPageLink = (
  query: SearchPageQueryType
): GeneratedLinkUrlObject => {
  const { page, ...restQuery } = query;
  if (page && page === 1) {
    return {
      pathname: '/search/',
      query: purgeNullAndUndefined(restQuery as SearchPageQueryType)
    };
  }
  return {
    pathname: '/search/',
    query: purgeNullAndUndefined(query)
  };
};

export const generateTradenamePageLink = (
  query: SearchPageQueryType,
  cityName?: string
): GeneratedLinkUrlObject => {
  const { page, drug, search, ...restQuery } = query;
  if (page && page === 1) {
    return {
      pathname: `/tradename/${drug}/${seoFriendlyCityParam(
        cityName
      )}${seoFriendlyPageParam(page)}`,
      query: purgeNullAndUndefined(restQuery as SearchPageQueryType)
    };
  }
  return {
    pathname: `/tradename/${drug}/${seoFriendlyCityParam(
      cityName
    )}${seoFriendlyPageParam(page)}`,
    query: purgeNullAndUndefined({ ...restQuery })
  };
};

export const generateCategoryPageLink = (
  query: CategoryPageQueryType
): GeneratedLinkUrlObject => {
  const { subCategoryId, category, page, ...otherQueryProps } = query || {};
  const subCategoryIdValue =
    subCategoryId || category?.subCategories[0]?.translitName;
  const categoryId = category?.translitName;
  return {
    // redirect to /category/subCategory (without pagination) if the requested page is the first one
    pathname: `/${categoryId}/${subCategoryIdValue}/${seoFriendlyPageParam(
      page
    )}`,
    query: otherQueryProps
  };
};

export const generateFirstLevelCategoryPageLink = (
  query: MultyLevelCategoryPageQueryType
): GeneratedLinkUrlObject => {
  const { category, page, ...otherQueryProps } = query || {};
  const categoryId = category?.translitName;
  return {
    // redirect to /category (without pagination) if the requested page is the first one
    pathname: `/${categoryId}/${seoFriendlyPageParam(page)}`,
    query: otherQueryProps
  };
};

export const generateSecondLevelCategoryPageLink = (
  query: MultyLevelCategoryPageQueryType
): GeneratedLinkUrlObject => {
  const { subCategoryId, category, page, ...otherQueryProps } = query || {};
  const categoryId = category?.translitName;
  const subCategoryIdValue =
    subCategoryId || category?.subCategories[0]?.translitName;
  return {
    // redirect to /category/subCategory (without pagination) if the requested page is the first one
    pathname: `/${categoryId}/${subCategoryIdValue}/${seoFriendlyPageParam(
      page
    )}`,
    query: otherQueryProps
  };
};

export const generateThirdLevelCategoryPageLink = (
  query: MultyLevelCategoryPageQueryType
): GeneratedLinkUrlObject => {
  const {
    secondCategoryId,
    thirdCategoryId,
    category,
    page,
    ...otherQueryProps
  } = query || {};
  const categoryId = category?.translitName;
  const secondCategoryIdValue =
    secondCategoryId || category?.subCategories[0]?.translitName;
  const thirdCategoryIdValue =
    thirdCategoryId ||
    category?.subCategories[0]?.subCategories[0]?.translitName;
  return {
    pathname: `/${categoryId}/${secondCategoryIdValue}/${thirdCategoryIdValue}/${seoFriendlyPageParam(
      page
    )}`,
    query: otherQueryProps
  };
};

export const generateProductsByLetterPage = (
  query: LetterPageQueryType
): {
  pathname: string;
} => {
  const { page, letter } = query || {};
  return {
    pathname: `/letter/${letter}/${seoFriendlyPageParam(page)}`
  };
};

export const generateCartPageLink = (step: BookingStep) => {
  return {
    pathname: `/checkout/cart?step=${step}`
  };
};

export const generateCatalogPageLink = () => `/catalog`;

export const generateBlogPage = (
  query: PageQueryType
): {
  pathname: string;
} => {
  const { page } = query || {};
  return {
    pathname: `blog/${seoFriendlyPageParam(page)}`
  };
};

export const generateBlogPageLink = (
  query: SearchPageQueryType
): GeneratedLinkUrlObject => {
  const { page, ...restQuery } = query;
  return {
    pathname: `blog/${seoFriendlyPageParam(page)}`,
    query: purgeNullAndUndefined({ ...restQuery })
  };
};

export const generateGoogleMapsRouteLink = (
  latitude: string,
  longitude: string
): string => {
  const baseUrl = 'https://www.google.com/maps/dir/?api=1&destination=';
  const destination = `${latitude},${longitude}`;

  return `${baseUrl}${destination}`;
};
